import { GetStaticPropsContext } from 'next';

import { AccreditationPageContent } from '~/features/accreditation/components/accreditation-page-content';
import { mastersAiContent } from '~/features/accreditation/configs/masters-ai-content';
import { PINNED_ELECTIVE_KEYS } from '~/features/accreditation/constants/woolf-display-order';
import { fetchAccreditationCorePrograms } from '~/features/accreditation/controllers/fetch-accreditation-core-programs';
import { fetchAccreditationElectives } from '~/features/accreditation/controllers/fetch-accreditation-electives';
import { AccreditationMilestonesProps } from '~/features/accreditation/types';
import { buildCorePrograms } from '~/features/accreditation/utils/build-core-programs';
import { orderByPinnedKeys } from '~/features/accreditation/utils/order-by-pinned-keys';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { useSessionReplay } from '~/features/session-replay';

export default function MastersAILandingPage({
  page,
  milestones,
}: {
  page: Page;
  milestones: AccreditationMilestonesProps;
}) {
  useSessionReplay();

  return (
    <>
      <PageHead page={page} />
      <PageLayout withoutVerticalPadding>
        <AccreditationPageContent {...mastersAiContent} milestones={milestones} />
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const page = await getPage('/masters-artificial-intelligence');

  if (!page) {
    return {
      notFound: true,
    };
  }

  // Woolf is the source of truth for which Tier 1 and Tier 2 programs render (CONENG-2483);
  // the repo only pins their display order. If either live fetch comes back empty, FAIL the
  // build rather than shipping a static page with an empty curriculum section. The upstream
  // Woolf error is logged by the controller before it returns `null`.
  //
  // The two fetches are independent and run concurrently to keep build time down.
  const [liveElectiveKeys, liveCore] = await Promise.all([
    fetchAccreditationElectives('ms_ai'),
    fetchAccreditationCorePrograms('ms_ai'),
  ]);

  if (!liveElectiveKeys || liveElectiveKeys.length === 0) {
    throw new Error(
      'getStaticProps for /masters-artificial-intelligence: Woolf Integration API returned no elective keys ' +
        '(formalDegreeKey=ms_ai). Refusing to ship a static page with no electives; see server logs for the upstream Woolf error.'
    );
  }

  if (!liveCore || liveCore.courses.length === 0) {
    throw new Error(
      'getStaticProps for /masters-artificial-intelligence: Woolf Integration API returned no core programs ' +
        '(formalDegreeKey=ms_ai). Refusing to ship a static page with no core modules; see server logs for the upstream Woolf error.'
    );
  }

  const electiveKeys = orderByPinnedKeys(liveElectiveKeys, (key) => key, PINNED_ELECTIVE_KEYS.ms_ai);
  const { corePrograms, woolfToUdacityProgramKeys } = buildCorePrograms('ms_ai', liveCore.courses);

  const milestones: AccreditationMilestonesProps = {
    ...mastersAiContent.milestones,
    electiveKeys,
    corePrograms,
    woolfToUdacityProgramKeys,
  };

  return {
    props: {
      page,
      milestones,
    },
  };
}
